import {
    api,
    apiErrorMessage,
    ME_TOKEN,
    RecordPeek,
    useAuthStore,
    type Prefetch,
} from '@bemor/web-kit';
import {
    AsOfBadge,
    Button,
    DateTime,
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DoneIcon,
    MailIcon,
    Money,
    ObjectRef,
    OnboardingIcon,
    PageHeader,
    RowActions,
    SearchableSelect,
    StatusBadge,
    statusLabel,
    StoreName,
    TasksIcon,
    type ListColumnDef,
    type ListViewPreset,
    type SelectOption,
} from '@bemor/web-kit/ui';
import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import type { components as adminApi } from '@/api/generated/admin';
import type {
    ChargebackRow,
    QueueItem,
    QueueName,
    StaffMember,
} from '@/api/types';
import { DetailBody } from '@/components/detail';
import { ObjectColorButton } from '@/components/object-colors';
import { QUEUES } from '@/layouts/app-layout';
import { BulkFailures, reportBulk, type BulkResult } from '@/lib/bulk';
import {
    AdminListPage,
    adminListPrefetch,
    filterDefs,
    useAdminList,
} from '@/lib/lists';
import { P } from '@/lib/permissions';
import { useOne, useWrite } from '@/lib/queries';
import { useMe, who } from '@/lib/staff';
import { ADMIN_STATUSES } from '@/lib/statuses';
import { OpenFromRowSheet } from '@/pages/disputes/open-dispute';
import { caseHref } from '@/pages/risk/reviews';

const DESCRIPTIONS: Record<QueueName, string> = {
    onboarding: 'Merchant applications submitted for review.',
    approvals: 'Actions waiting for a second pair of eyes.',
    disputes: 'Chargebacks and retrievals opened on a merchant.',
    alerts: 'Alerts Reporting raised on the platform or a merchant.',
    reconciliation: 'Gaps between the processor and the ledger.',
    kill_switches: 'Kill switches flipped, with their cases.',
    offboarding: 'Merchants closed, tracked until every service erased them.',
    payment_reviews: 'Held payments waiting for a staff decision.',
};

/** Where a queue item's subject lives in the app. */
export function subjectPath(type: string, id: string): string | null {
    switch (type) {
        case 'merchant':
        case 'application':
            return `/merchants/${id}`;
        case 'approval':
            return `/approvals/${id}`;
        // BM-127 decision 5: dispute_evidence_overdue, the merchant missed the evidence deadline
        case 'dispute':
        case 'dispute_evidence_overdue':
            return `/disputes/${id}`;
        // BM-127: an NMI chargeback row no charge (or several) matched; staff open it from the peek or the tab
        case 'chargeback_row':
            return '/disputes?tab=unmatched';
        case 'case':
            return `/cases/${id}`;
        case 'flag':
            return `/flags/${id}`;
        case 'alert':
            return '/alerts';
        // the item's subject id is the review's public id (crv_); the case page reads a case by it (BM-125 decision 20)
        case 'charge_review':
            return caseHref(id);
        default:
            return null;
    }
}

/** Built-in views of every queue: what is open (the default), what is mine, what is done. */
export const QUEUE_PRESETS: ListViewPreset[] = [
    { key: 'open', name: 'Open', filters: { state: ['open'] } },
    {
        key: 'mine',
        name: 'Mine',
        filters: { state: ['open'], assigned_to: [ME_TOKEN] },
    },
    { key: 'done', name: 'Done', filters: { state: ['done'] } },
];

const queueOf = (params: Record<string, string | undefined>) =>
    params.queue ?? 'onboarding';
const isQueue = (name: string) => (QUEUES as readonly string[]).includes(name);

/** The route's prefetch: the person's views of this queue, then its rows (an unknown queue reads nothing). */
export const queuePrefetch: Prefetch = (context) => {
    const name = queueOf(context.params);
    if (!isQueue(name)) return [];
    return adminListPrefetch(`queues.${name}`, PATH, {
        presets: QUEUE_PRESETS,
        pathOf: () => `/staff/queues/${name}`,
    })(context);
};

const PATH = '/staff/queues/{queue}';

type QueueBulkRequest = adminApi['schemas']['QueueItemsBulkRequest'];

/** `POST /staff/queue-items/bulk`: each id through the single assign / done, one result per id (BM-46 §2.4). */
export async function queueBulk(body: QueueBulkRequest): Promise<BulkResult[]> {
    return (await api().post<BulkResult[]>('/staff/queue-items/bulk', body))
        .data;
}

/** Staff to assign to, searched by name, email or id on the staff list (needs identity.staff.manage). */
async function staffOptions(q: string): Promise<SelectOption[]> {
    const page = await api().list<StaffMember>('/staff/staff', {
        params: { q, status: ['active'], per_page: 20 },
    });
    return page.items.map((m) => ({
        value: m.user.id,
        label: m.user.name,
        description: m.user.email,
    }));
}

/** Pick the person the selected items go to. */
function AssignDialog({
    open,
    count,
    busy,
    onClose,
    onAssign,
}: {
    open: boolean;
    count: number;
    busy: boolean;
    onClose: () => void;
    onAssign: (staffUserId: string) => void;
}) {
    const [who, setWho] = useState<string | null>(null);
    return (
        <Dialog open={open} onOpenChange={(o) => (o ? null : onClose())}>
            <DialogContent data-testid="assign-dialog">
                <DialogHeader>
                    <DialogTitle>
                        Assign {count === 1 ? 'one item' : `${count} items`}
                    </DialogTitle>
                    <DialogDescription>
                        The person you choose sees them under Mine.
                    </DialogDescription>
                </DialogHeader>
                <SearchableSelect
                    label="Staff member"
                    loadOptions={staffOptions}
                    value={who}
                    onChange={setWho}
                    placeholder="Choose a staff member"
                />
                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>
                        Cancel
                    </Button>
                    <Button
                        disabled={!who || busy}
                        onClick={() => who && onAssign(who)}
                    >
                        Assign
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

/** BM-127: the chargeback row behind an alerts item, with "Open dispute…" while it is unmatched. */
function ChargebackRowPeek({ rowId, open }: { rowId: string; open: boolean }) {
    const can = useAuthStore((s) => s.can);
    const query = useOne<ChargebackRow>(`/staff/disputes/rows/${rowId}`);
    const [opening, setOpening] = useState(false);
    // review: loading, a failed read (404, 403) and retry through DetailBody, as the approvals peek does
    return (
        <DetailBody query={query} title="Chargeback">
            {(r) => (
                <div className="space-y-2" data-testid="chargeback-row-peek">
                    <p className="m-0">
                        <Money cents={r.amount_cents} />, card{' '}
                        <span className="font-mono text-xs">
                            {r.card_bin ?? '······'}…{r.card_last4 ?? '····'}
                        </span>
                        , {r.reason ?? 'no reason'} ({r.candidates.length}{' '}
                        matching
                        {r.candidates.length === 1 ? ' charge' : ' charges'})
                    </p>
                    {open &&
                    r.state === 'unmatched' &&
                    can(P.disputesRespond) ? (
                        <Button size="sm" onClick={() => setOpening(true)}>
                            Open dispute…
                        </Button>
                    ) : null}
                    {opening ? (
                        <OpenFromRowSheet
                            row={r}
                            onClose={() => setOpening(false)}
                        />
                    ) : null}
                </div>
            )}
        </DetailBody>
    );
}

/** A queue item's fields, in the peek sheet. */
function QueueItemPeek({ item, me }: { item: QueueItem; me: string }) {
    const to = subjectPath(item.subject_type, item.subject_id);
    return (
        <dl className="grid grid-cols-1 gap-3 text-sm" data-testid="queue-peek">
            <div>
                <dt className="text-muted-foreground">Item</dt>
                <dd className="font-medium">{item.title}</dd>
            </div>
            <div>
                <dt className="text-muted-foreground">State</dt>
                <dd>
                    <StatusBadge
                        status={item.state}
                        domain={ADMIN_STATUSES.queueItem}
                    />
                </dd>
            </div>
            <div>
                <dt className="text-muted-foreground">Subject</dt>
                <dd>
                    {to ? (
                        <Link
                            to={to}
                            className="font-mono text-xs hover:underline"
                        >
                            {item.subject_type} {item.subject_id}
                        </Link>
                    ) : (
                        <span className="font-mono text-xs">
                            {item.subject_type} {item.subject_id}
                        </span>
                    )}
                </dd>
            </div>
            <div>
                <dt className="text-muted-foreground">Merchant</dt>
                <dd className="font-mono text-xs">{item.merchant_id ?? '—'}</dd>
            </div>
            {item.store ? (
                <div>
                    <dt className="text-muted-foreground">Store</dt>
                    <dd>
                        <StoreName store={item.store} />
                    </dd>
                </div>
            ) : null}
            <div>
                <dt className="text-muted-foreground">Assigned to</dt>
                <dd>{who(item.assigned_to, me)}</dd>
            </div>
            <div>
                <dt className="text-muted-foreground">Opened</dt>
                <dd>
                    <DateTime value={item.created_at} empty="—" />
                </dd>
            </div>
            {item.subject_type === 'chargeback_row' ? (
                <div>
                    <dt className="text-muted-foreground">Chargeback</dt>
                    <dd>
                        <ChargebackRowPeek
                            rowId={item.subject_id}
                            open={item.state === 'open'}
                        />
                    </dd>
                </div>
            ) : null}
            {item.done_at ? (
                <div>
                    <dt className="text-muted-foreground">Done</dt>
                    <dd>
                        <DateTime value={item.done_at} empty="—" /> by{' '}
                        {who(item.done_by, me)}
                    </dd>
                </div>
            ) : null}
        </dl>
    );
}

export function QueuePage() {
    const { queue = 'onboarding' } = useParams();
    const me = useMe();
    const can = useAuthStore((s) => s.can);
    const known = isQueue(queue);
    const [error, setError] = useState<string | null>(null);
    const assign = useWrite(
        async (body: { item: QueueItem; assigned_to: string | null }) =>
            (
                await api().post<QueueItem>(
                    `/staff/queues/${queue}/items/${body.item.id}/assign`,
                    {
                        assigned_to: body.assigned_to,
                        version: body.item.version,
                    },
                )
            ).data,
        [`/staff/queues`],
    );
    const done = useWrite(
        async (item: QueueItem) =>
            (
                await api().post<QueueItem>(
                    `/staff/queues/${queue}/items/${item.id}/done`,
                    { version: item.version },
                )
            ).data,
        [`/staff/queues`],
    );
    // BM-66 (decision 6): the reviewer asked the subject for more, or took the answer; the item moves column
    const needsInfo = useWrite(
        async (body: { item: QueueItem; answered: boolean }) =>
            (
                await api().post<QueueItem>(
                    `/staff/queues/${queue}/items/${body.item.id}/needs-info`,
                    { version: body.item.version, answered: body.answered },
                )
            ).data,
        [`/staff/queues`],
    );
    const onError = (e: unknown) => setError(apiErrorMessage(e));
    // BM-145: a Ledger refusal goes again: its dead delivery is re-queued and the item closes
    const retryLedger = useWrite(
        async (item: QueueItem) =>
            (
                await api().post<{ requeued: number }>(
                    `/staff/ledger-refusals/${item.subject_id}/retry`,
                )
            ).data,
        [`/staff/queues`],
    );
    const [selected, setSelected] = useState<string[]>([]);
    const [failures, setFailures] = useState<BulkResult[]>([]);
    const [assigning, setAssigning] = useState(false);
    const bulk = useWrite(queueBulk, ['/staff/queues']);
    const mayAssign = can(P.casesAssign);
    const mayFinish = can(P.casesWrite);
    const mayRetryLedger = can(P.ledgerManage);
    // BM-46 §2.5: a queue's color (admin.cases.write)
    const canColor = can(P.casesWrite);
    const run = (body: Omit<QueueBulkRequest, 'ids'>, verb: string) =>
        bulk.mutate(
            { ...body, ids: selected },
            {
                onSuccess: (results) => {
                    setAssigning(false);
                    setFailures(reportBulk(results, verb, setSelected));
                },
                onError,
            },
        );
    const columns: ListColumnDef<QueueItem>[] = [
        {
            key: 'title',
            label: 'Item',
            sortKey: 'title',
            cell: (i) => {
                const to = subjectPath(i.subject_type, i.subject_id);
                return to ? (
                    <Link
                        to={to}
                        className="hover:underline"
                        onClick={(e) => e.stopPropagation()}
                    >
                        {i.title}
                    </Link>
                ) : (
                    i.title
                );
            },
            locked: 'start',
            boardCard: true,
        },
        {
            key: 'subject',
            label: 'Subject',
            cell: (i) => (
                <span className="font-mono text-xs">
                    {i.subject_type} {i.subject_id}
                </span>
            ),
        },
        {
            key: 'merchant_id',
            aliases: ['merchant'],
            label: 'Merchant',
            sortKey: 'merchant_id',
            cell: (i) =>
                i.merchant_id ? <ObjectRef id={i.merchant_id} /> : '—',
            boardCard: true,
        },
        {
            // BM-87: the store of the charge the item came from (a review, a dispute, a failed tax commit); empty otherwise
            key: 'store',
            label: 'Store',
            cell: (i) => (i.store ? <StoreName store={i.store} /> : '—'),
        },
        {
            key: 'assigned_to',
            aliases: ['assigned'],
            label: 'Assigned to',
            sortKey: 'assigned_to',
            cell: (i) => who(i.assigned_to, me),
            boardCard: true,
        },
        {
            key: 'status',
            label: 'Status',
            sortKey: 'status',
            // BM-66 (design 0.5.0-c board): new, under review, needs info, done; the board keys its columns on it
            cell: (i) => <StatusBadge status={i.status} domain="queue_item" />,
            groupable: { key: 'status', value: (i) => i.status },
            boardCard: true,
        },
        {
            key: 'state',
            label: 'State',
            sortKey: 'state',
            cell: (i) => (
                <StatusBadge
                    status={i.state}
                    domain={ADMIN_STATUSES.queueItem}
                />
            ),
            groupable: { key: 'state', value: (i) => i.state },
        },
        {
            key: 'created_at',
            aliases: ['created'],
            label: 'Opened',
            kind: 'datetime',
            sortKey: 'created_at',
            cell: (i) => <DateTime value={i.created_at} empty="—" />,
            noTruncate: true,
        },
        {
            key: 'actions',
            label: 'Actions',
            locked: 'end',
            actions: true,
            // BM-84 (rule G.6, design 0.7.0 queue): Take, Needs info / Answered, Open case, Mark done in one menu
            cell: (i) => (
                <RowActions
                    name={i.title}
                    items={
                        i.state !== 'open'
                            ? []
                            : [
                                  {
                                      label: 'Take',
                                      icon: OnboardingIcon,
                                      hidden:
                                          !can(P.casesAssign) ||
                                          i.assigned_to === me,
                                      onSelect: () =>
                                          assign.mutate(
                                              { item: i, assigned_to: me },
                                              { onError },
                                          ),
                                  },
                                  {
                                      // BM-66 (decision 6): ask the subject for more, or take the answer; the item moves column
                                      label:
                                          i.status === 'needs_info'
                                              ? 'Answered'
                                              : 'Needs info',
                                      icon: MailIcon,
                                      hidden: !can(P.casesWrite),
                                      disabled: needsInfo.isPending,
                                      onSelect: () =>
                                          needsInfo.mutate(
                                              {
                                                  item: i,
                                                  answered:
                                                      i.status === 'needs_info',
                                              },
                                              { onError },
                                          ),
                                  },
                                  {
                                      label: 'Open case',
                                      icon: TasksIcon,
                                      hidden: !i.case_id,
                                      to: `/cases/${i.case_id}`,
                                  },
                                  {
                                      label: 'Mark done',
                                      icon: DoneIcon,
                                      hidden: !can(P.casesWrite),
                                      onSelect: () =>
                                          done.mutate(i, { onError }),
                                  },
                              ]
                    }
                />
            ),
        },
    ];
    const { state, query } = useAdminList<QueueItem>(
        `queues.${queue}`,
        PATH,
        {
            path: `/staff/queues/${queue}`,
            columns,
            presets: QUEUE_PRESETS,
            filters: filterDefs(PATH, {
                q: 'Search',
                state: 'State',
                status: 'Status',
                assigned_to: 'Assignee',
                range: 'Opened',
            }),
        },
        known,
    );
    if (!known)
        return (
            <p className="text-muted-foreground p-6 text-center text-sm">
                There is no queue called {queue}.
            </p>
        );
    const peeked = query.rows.find((i) => i.id === state.peek);
    return (
        <div>
            <PageHeader
                title={`${statusLabel(queue)} queue`}
                secondaryAction={
                    canColor ? (
                        <ObjectColorButton
                            type="queue"
                            id={queue}
                            label={`${statusLabel(queue)} queue`}
                        />
                    ) : null
                }
                meta={
                    <span className="flex flex-col gap-1">
                        <span>{DESCRIPTIONS[queue as QueueName]}</span>
                        <AsOfBadge asOf={query.freshness.asOf} />
                    </span>
                }
            />
            {error ? (
                <p role="alert" className="text-destructive mb-3 text-sm">
                    {error}
                </p>
            ) : null}
            <AdminListPage
                noun="queue items"
                state={state}
                query={query}
                rowKey={(i) => i.id}
                onRow={(i) => state.setPeek(i.id)}
                selection={
                    mayAssign || mayFinish
                        ? {
                              selected,
                              onChange: setSelected,
                              label: (i) => i.title,
                          }
                        : undefined
                }
                bulk={{
                    noun: { one: 'item', other: 'items' },
                    actions: (
                        <>
                            {mayAssign ? (
                                <>
                                    <Button
                                        size="sm"
                                        variant="outline"
                                        disabled={bulk.isPending}
                                        onClick={() =>
                                            run(
                                                {
                                                    action: 'assign',
                                                    assignee_id: 'me',
                                                },
                                                'assigned',
                                            )
                                        }
                                    >
                                        Assign to me
                                    </Button>
                                    {can(P.staffManage) ? (
                                        <Button
                                            size="sm"
                                            variant="outline"
                                            disabled={bulk.isPending}
                                            onClick={() => setAssigning(true)}
                                        >
                                            Assign to…
                                        </Button>
                                    ) : null}
                                    <Button
                                        size="sm"
                                        variant="outline"
                                        disabled={bulk.isPending}
                                        onClick={() =>
                                            run(
                                                { action: 'unassign' },
                                                'unassigned',
                                            )
                                        }
                                    >
                                        Unassign
                                    </Button>
                                </>
                            ) : null}
                            {mayFinish ? (
                                <Button
                                    size="sm"
                                    variant="outline"
                                    disabled={bulk.isPending}
                                    onClick={() =>
                                        run(
                                            { action: 'needs_info' },
                                            'marked needs info',
                                        )
                                    }
                                >
                                    Needs info
                                </Button>
                            ) : null}
                            {mayFinish ? (
                                <Button
                                    size="sm"
                                    disabled={bulk.isPending}
                                    onClick={() =>
                                        run({ action: 'done' }, 'done')
                                    }
                                >
                                    Mark done
                                </Button>
                            ) : null}
                        </>
                    ),
                }}
                controls={{
                    q: { kind: 'search' },
                    state: { domain: ADMIN_STATUSES.queueItem },
                    status: { domain: 'queue_item' },
                    assigned_to: {
                        kind: 'enum',
                        options: [{ value: ME_TOKEN, label: 'Mine' }],
                    },
                }}
                empty={
                    <p className="text-muted-foreground p-6 text-center text-sm">
                        The queue is empty.
                    </p>
                }
            />
            <AssignDialog
                open={assigning}
                count={selected.length}
                busy={bulk.isPending}
                onClose={() => setAssigning(false)}
                onAssign={(who) =>
                    run({ action: 'assign', assignee_id: who }, 'assigned')
                }
            />
            <BulkFailures
                failures={failures}
                labelOf={(id) =>
                    query.rows.find((i) => i.id === id)?.title ?? id
                }
                onClose={() => setFailures([])}
            />
            <RecordPeek
                state={state}
                title={peeked?.title ?? 'Queue item'}
                href={() =>
                    (peeked &&
                        subjectPath(peeked.subject_type, peeked.subject_id)) ??
                    ''
                }
            >
                {() =>
                    peeked ? (
                        <div className="flex flex-col gap-4">
                            <QueueItemPeek item={peeked} me={me} />
                            {peeked.state === 'open' &&
                            (mayFinish ||
                                (mayAssign && peeked.assigned_to !== me) ||
                                (mayRetryLedger &&
                                    peeked.subject_type ===
                                        'ledger_refusal')) ? (
                                <div
                                    className="flex flex-wrap gap-2"
                                    data-testid="queue-peek-actions"
                                >
                                    {mayAssign && peeked.assigned_to !== me ? (
                                        <Button
                                            size="sm"
                                            variant="outline"
                                            onClick={() =>
                                                assign.mutate(
                                                    {
                                                        item: peeked,
                                                        assigned_to: me,
                                                    },
                                                    { onError },
                                                )
                                            }
                                        >
                                            Take
                                        </Button>
                                    ) : null}
                                    {mayRetryLedger &&
                                    peeked.subject_type === 'ledger_refusal' ? (
                                        <Button
                                            size="sm"
                                            variant="outline"
                                            disabled={retryLedger.isPending}
                                            data-testid="ledger-refusal-retry"
                                            onClick={() =>
                                                retryLedger.mutate(peeked, {
                                                    onError,
                                                })
                                            }
                                        >
                                            Retry delivery
                                        </Button>
                                    ) : null}
                                    {mayFinish ? (
                                        <Button
                                            size="sm"
                                            onClick={() =>
                                                done.mutate(peeked, { onError })
                                            }
                                        >
                                            Done
                                        </Button>
                                    ) : null}
                                </div>
                            ) : null}
                        </div>
                    ) : (
                        <p className="text-muted-foreground text-sm">
                            This item is not on the current page.
                        </p>
                    )
                }
            </RecordPeek>
        </div>
    );
}
